import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";

import { Download, Loader2 } from "lucide-react";
//@ts-ignore
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BASE_URL, Image_BASE_URL } from "@/api/apiUrl";
import { useState } from "react";
// import People from "@/assets/icons/People";
import { Button } from "../ui/button";
import Cookies from "js-cookie";
import axios from "axios";
import { usePagination } from "@/hooks/use-pagination";
import TableLoader from "./TableLoader";
import CustomPagination from "./CustomPagination";
import { CusotmPopoverComponent } from "./CustomPopoverComponent";

interface User {
  id: string;
  season: {
    title: string;
    eventDate: string;
  };
  totalAmount: number;
  event_participants: EventParticipant[];
  status: string;
  payments: [
    {
      id: string;
      invoices: [
        {
          invoiceNumber: string;
        }
      ];
    }
  ];
}

export interface EventParticipant {
  id: string;
  member: {
    name: string;
    user: {
      status: string;
    };
  };
  category: {
    name: string;
    price: number;
  };
}

const tableHeader = [
  "Title",
  "Date",
  "Total Amount",
  "Payment Status",
  "Event Participants",
  "Download Invoice",
];

const MyRegistrationComponent = () => {
  const [pageSize, _] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    remainingPages: -1,
  });

  const fetchMyRegistration = async () => {
    const token = Cookies.get("token");
    const response = await axios.get(
      `${BASE_URL}/registration/me?pageNo=${currentPage}&pageSize=${pageSize}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    setPagination(response.data.pagination);
    return response.data.data;
  };

  const { data, isPending } = useQuery({
    queryKey: ["my-registration", currentPage, pageSize],
    queryFn: fetchMyRegistration,
  });

  // State for tracking the loading and error messages for each invoice
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>(
    {}
  );

  // Function to check if the PDF is available
  const checkPDFStatus = async (url: string): Promise<boolean> => {
    try {
      const response = await fetch(url, { method: "HEAD" });
      return response.ok;
    } catch (error) {
      console.error("Error checking PDF status:", error);
      return false;
    }
  };

  // Handle PDF Download
  const handleDownload = async (
    url: string,
    filename: string,
    invoiceId: string
  ) => {
    setLoadingStates((prev) => ({ ...prev, [invoiceId]: true }));
    setErrorMessages((prev) => ({ ...prev, [invoiceId]: "" }));

    let isPDFReady = false;
    const maxRetries = 3;
    const retryInterval = 1000; // Retry every 2 seconds

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      isPDFReady = await checkPDFStatus(url);
      if (isPDFReady) break;
      await new Promise((resolve) => setTimeout(resolve, retryInterval));
    }

    if (isPDFReady) {
      // Download the file
      fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const fileURL = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = fileURL;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
        })
        .catch((error) => {
          console.error("Error downloading the file:", error);
          setErrorMessages((prev) => ({
            ...prev,
            [invoiceId]: "Failed to download the file.",
          }));
        })
        .finally(() => {
          setLoadingStates((prev) => ({ ...prev, [invoiceId]: false }));
        });
    } else {
      setErrorMessages((prev) => ({
        ...prev,
        [invoiceId]: "Can't download Invoice.",
      }));
      setLoadingStates((prev) => ({ ...prev, [invoiceId]: false }));
    }
  };
  const { totalPages, totalItems: totalResults } = pagination;
  const { pages, showLeftEllipsis, showRightEllipsis } = usePagination({
    currentPage,
    totalPages,
    paginationItemsToDisplay: 4,
  });

  const handlePageChange = (page: any) => {
    setCurrentPage(page);
  };

  if (isPending) {
    return <TableLoader />;
  }

  return (
    <section className="pb-36 mx-auto max-w-6xl">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-[#33B9BA] to-[#00A3A8]">
          <h1 className="text-2xl font-bold text-white">Registrations</h1>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {tableHeader.map((header) => (
                    <TableHead
                      key={header}
                      className="bg-[#33B9BA] text-white font-semibold"
                    >
                      {header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((user: User) => {
                  const invoice = user.payments[0]?.invoices[0];
                  const invoiceId = invoice?.invoiceNumber;
                  const pdfUrl = `${Image_BASE_URL}/invoices/${invoiceId}.pdf`;
                  const uniqueKey = user.id;
                  return (
                    <TableRow key={user.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium text-[#595959]">
                        {user.season.title}
                      </TableCell>
                      <TableCell>
                        {moment(user.season.eventDate).format("Do MMMM YYYY")}
                      </TableCell>
                      <TableCell>{user.totalAmount / 100}</TableCell>
                      <TableCell>
                        <TooltipProvider delayDuration={50}>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge
                                className={cn(
                                  user.status === "COMPLETED"
                                    ? "bg-green-400 text-white"
                                    : user.status === "PENDING"
                                    ? "bg-red-400 text-white"
                                    : "bg-gray-200 text-black"
                                )}
                              >
                                {user?.status === "PENDING" && <p>FAILED</p>}
                                {user?.status === "COMPLETED" && (
                                  <p>COMPLETED</p>
                                )}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <p className="text-sm">
                                Check info icon for Aadhar verification
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <CusotmPopoverComponent
                          evntParticipants={user.event_participants}
                        />
                      </TableCell>
                      <TableCell>
                        {user.status === "COMPLETED" && invoiceId ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className={`text-[#595959] ${
                              loadingStates[uniqueKey]
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                            onClick={() =>
                              !loadingStates[uniqueKey] &&
                              handleDownload(
                                pdfUrl,
                                `invoice_${invoiceId}.pdf`,
                                uniqueKey
                              )
                            }
                            disabled={loadingStates[uniqueKey]}
                          >
                            {loadingStates[uniqueKey] ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="mr-2 h-4 w-4" />
                            )}
                            Download
                          </Button>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                        {errorMessages[uniqueKey] && (
                          <p className="text-red-500 text-xs mt-1">
                            {errorMessages[uniqueKey]}
                          </p>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="w-full flex justify-between items-center mt-4 bg-gray-50 p-4 rounded-lg">
              <div className="w-full text-xs text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1} to{" "}
                {Math.min(pageSize * currentPage, totalResults)} of{" "}
                {totalResults} results
              </div>

              <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                showLeftEllipsis={showLeftEllipsis}
                showRightEllipsis={showRightEllipsis}
                pages={pages}
                handlePageChange={handlePageChange}
              />
            </div>
            <div className="overflow-x-auto">
              {data.length === 0 && (
                <div className="w-full p-4">
                  <p className="text-center">No Registrations Available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MyRegistrationComponent;
